// src/services/syncService.js
import axios from 'axios';
import AppConst from '../apis/appconst';

class SyncService {
    constructor() {
        this.syncInterval = null;
        this.backupInterval = null;
        this.isSyncing = false;
        this.isBackingUp = false;
        this.onSyncComplete = null;
        this.onSyncError = null;
        this.onSyncStart = null;
        this.onBackupComplete = null;
        this.onBackupError = null;
    }

    // =========================================================
    // AUTO-SYNC: Local → Online (every 30 sec)
    // =========================================================
    startAutoSync(onComplete, onError, onStart) {
        this.onSyncComplete = onComplete;
        this.onSyncError = onError;
        this.onSyncStart = onStart;

        if (this.syncInterval) {
            console.log('[SyncService] Auto-sync already running');
            return;
        }

        console.log('[SyncService] Starting auto-sync (Local → Online)');
        this.performSync();

        this.syncInterval = setInterval(async () => {
            if (navigator.onLine && !this.isSyncing) {
                await this.performSync();
            }
        }, 30000);
    }

    stopAutoSync() {
        if (this.syncInterval) {
            clearInterval(this.syncInterval);
            this.syncInterval = null;
            console.log('[SyncService] Auto-sync stopped');
        }
    }

    // =========================================================
    // AUTO-BACKUP: Online → Local (every 30 sec)
    // =========================================================
    startAutoBackup(onComplete, onError) {
        this.onBackupComplete = onComplete;
        this.onBackupError = onError;

        if (this.backupInterval) {
            console.log('[SyncService] Auto-backup already running');
            return;
        }

        console.log('[SyncService] Starting auto-backup (Online → Local)');
        this.performBackup();

        this.backupInterval = setInterval(async () => {
            if (navigator.onLine && !this.isBackingUp) {
                await this.performBackup();
            }
        }, 30000);
    }

    stopAutoBackup() {
        if (this.backupInterval) {
            clearInterval(this.backupInterval);
            this.backupInterval = null;
            console.log('[SyncService] Auto-backup stopped');
        }
    }

    // =========================================================
    // Backup: Online → Local
    //
    // FIX: No separate health check GET before POST.
    // `php spark serve` is SINGLE-THREADED — if you do
    // GET /health then immediately POST /sync_from_online,
    // the POST arrives while GET is still being processed
    // → connection refused → browser shows "CORS error"
    //
    // Solution: Just POST directly. If server is down,
    // axios will throw a network error — we handle it.
    // =========================================================
    async performBackup() {
        if (this.isBackingUp) {
            console.log('[SyncService] Backup already in progress');
            return { backed_up: false, reason: 'already_backing_up' };
        }

        this.isBackingUp = true;

        try {
            console.log('[SyncService] Backing up Online → Local...');

            const response = await axios.post(
                `${AppConst.LOCAL_URL}${AppConst.syncFromOnlineUrl}`,
                {},
                {
                    headers: { 'Content-Type': 'application/json' },
                    timeout: 120000
                }
            );

            console.log('[SyncService] Backup completed:', response.data);

            if (this.onBackupComplete) {
                this.onBackupComplete(response.data);
            }

            return { backed_up: true, data: response.data };
        } catch (error) {
            if (!error.response) {
                console.log('[SyncService] Local server not reachable - skipping backup');
                return { backed_up: false, reason: 'local_server_offline' };
            }
            console.error('[SyncService] Backup failed:', error.message);

            if (this.onBackupError) {
                this.onBackupError(error);
            }

            return { backed_up: false, error: error.message };
        } finally {
            this.isBackingUp = false;
        }
    }

    // =========================================================
    // Pending count — direct POST, no health check
    // =========================================================
    async getPendingCount() {
        try {
            const response = await axios.post(
                `${AppConst.LOCAL_URL}${AppConst.syncPendingCountUrl}`,
                {},
                {
                    headers: { 'Content-Type': 'application/json' },
                    timeout: 5000
                }
            );
            return response.data?.total_pending || 0;
        } catch (error) {
            console.log('[SyncService] Pending count unavailable');
            return 0;
        }
    }

    // =========================================================
    // Sync: Local → Online — direct POST, no health check
    // =========================================================
    async performSync() {
        if (this.isSyncing) {
            console.log('[SyncService] Sync already in progress');
            return { synced: false, reason: 'already_syncing' };
        }

        this.isSyncing = true;

        try {
            console.log('[SyncService] Checking pending count...');
            const pendingCount = await this.getPendingCount();
            console.log(`[SyncService] Pending records: ${pendingCount}`);

            if (pendingCount === 0) {
                console.log('[SyncService] No pending data to sync');
                this.isSyncing = false;
                return { synced: true, pending: 0 };
            }

            if (this.onSyncStart) {
                this.onSyncStart(pendingCount);
            }

            console.log(`[SyncService] Syncing ${pendingCount} records...`);

            const response = await axios.post(
                `${AppConst.LOCAL_URL}${AppConst.syncToOnlineUrl}`,
                {},
                {
                    headers: { 'Content-Type': 'application/json' },
                    timeout: 60000
                }
            );

            console.log('[SyncService] Sync completed:', response.data);

            if (this.onSyncComplete) {
                this.onSyncComplete(response.data);
            }

            return { synced: true, data: response.data };
        } catch (error) {
            if (!error.response) {
                console.log('[SyncService] Local server not reachable - skipping sync');
                return { synced: false, reason: 'local_server_offline' };
            }
            console.error('[SyncService] Sync failed:', error.message);

            if (this.onSyncError) {
                this.onSyncError(error);
            }

            return { synced: false, error: error.message };
        } finally {
            this.isSyncing = false;
        }
    }

    async manualSync() {
        console.log('[SyncService] Manual sync triggered');
        return await this.performSync();
    }

    async manualBackup() {
        console.log('[SyncService] Manual backup triggered');
        return await this.performBackup();
    }

    // Sequential — important for single-threaded server
    async fullSync() {
        console.log('[SyncService] Full bidirectional sync...');
        const syncResult = await this.performSync();
        const backupResult = await this.performBackup();
        return { sync: syncResult, backup: backupResult };
    }

    async getSyncStatus() {
        try {
            const response = await axios.post(
                `${AppConst.LOCAL_URL}${AppConst.syncStatusUrl}`,
                {},
                {
                    headers: { 'Content-Type': 'application/json' },
                    timeout: 5000
                }
            );
            return response.data;
        } catch (error) {
            return null;
        }
    }

    stopAll() {
        this.stopAutoSync();
        this.stopAutoBackup();
    }
}

export default new SyncService();